<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name, position: 'left', allowOtherTop: true }"
    :button-bind="{ verboseName }"
    type="menu"
    has-close-header
  >
    <template #[`content.header.append`]>
      <!-- NAVBAR -->
      <span>
        <data-input ref="geometry-input" v-bind="geometryInput" @change="onGeometryChanged" />
      </span>
      <v-icon color="error" :disabled="isParcelLoading" @click="clearGeometry">mdi-cancel</v-icon>
    </template>
    <template #content>
      <!-- BASIC PARCEL INFO -->
      <div class="pa-2">
        <div v-for="(tuple, idx) in infos" :key="idx" no-gutters class="text-left d-flex" style="gap: 0.5rem">
          <span
            v-for="([key, value], tupleIdx) in tuple"
            :key="`${idx}_${tupleIdx}`"
            class="text-body-2 d-flex"
            style="gap: 0.3rem"
          >
            <span style="opacity: 0.6">{{ $i18n.t(`sidebar.areaReport.${key}`) }}:</span
            ><span class="font-weight-medium">{{ value }}</span>
          </span>
        </div>
        <div class="mt-4">
          <v-expansion-panels v-model="openedPanel" multiple accordion focusable :disabled="!isParcelFetched">
            <!-- COVERAGE SECTION -->
            <v-expansion-panel>
              <v-expansion-panel-header class="text-body-2">
                <div class="d-flex" style="gap: 0.5rem">
                  <span>
                    {{ $i18n.t('sidebar.areaReport.coverage') }}
                  </span>
                  <v-tooltip bottom max-width="500" color="font">
                    <template #activator="{ on }">
                      <v-icon v-on="on" small color="primary">
                        mdi-information
                      </v-icon>
                    </template>
                    {{ $i18n.t('sidebar.areaReport.coverageTooltip') }}
                  </v-tooltip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <div class="px-2 py-3">
                  <div class="d-flex align-center" style="gap: 1rem">
                    <data-input
                      class="flex-grow-1"
                      style="width: 100%; min-width: 0"
                      v-model="coverageData.layer"
                      @change="changeCoverageLayer"
                      v-bind="selectInputProps"
                      :items="areaProjectLayers"
                      itemText="name"
                      itemValue="layer_id"
                      :label="$i18n.t('sidebar.areaReport.layer')"
                    />
                    <data-input
                      class="flex-grow-1"
                      style="width: 100%; min-width: 0"
                      v-model="coverageData.attribute"
                      v-bind="selectInputProps"
                      :items="coverageLayerAttributes"
                      itemText="label"
                      itemValue="id"
                      :label="$i18n.t('sidebar.areaReport.attribute')"
                    />
                    <v-btn
                      @click="handleCoverage"
                      :disabled="!coverageData.layer || !coverageData.attribute"
                      :loading="isCoverageLoading"
                      icon
                    >
                      <v-icon color="primary">mdi-play</v-icon>
                    </v-btn>
                  </div>
                  <template v-if="coverage">
                    <div v-if="!coverage.length" class="mb-4 mt-6 text-caption text--disabled">
                      {{ $i18n.t('sidebar.areaReport.noCoverage') }}
                    </div>
                    <div v-else class="mt-4 text-left text-body-2">
                      <div>
                        <v-data-table
                          class="land-cover-table"
                          :headers="coverageTableHeaders"
                          dense
                          :items="sortedCoverage"
                          hide-default-footer
                        >
                          <template v-for="headerName in ['area', 'coverage']" #[`header.${headerName}`]="{ header }">
                            <div :key="headerName" class="d-flex align-center justify-center">
                              <span>{{ header.text }}</span>
                              <v-tooltip :value="tooltipAnchorId === headerName" bottom color="primary">
                                <template v-slot:activator="{ attrs }">
                                  <v-btn
                                    color="primary"
                                    v-bind="attrs"
                                    @click="
                                      $event =>
                                        copyToClipboard(
                                          $event,
                                          getCoverageText(headerName === 'coverage' ? ['area', 'coverage'] : ['area']),
                                          headerName
                                        )
                                    "
                                    icon
                                    small
                                  >
                                    <v-icon small>mdi-content-copy</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ $i18n.t('tooltips.valueCopied') }}</span>
                              </v-tooltip>
                            </div>
                          </template>
                          <template v-for="(header, idx) in coverageTableHeaders" #[`item.${header.value}`]="{ item }">
                            <span :key="idx">{{ getValueText(header.value, item[header.value]) }}</span>
                          </template>
                        </v-data-table>
                      </div>
                    </div>
                    <div style="max-height: 170px; height: 170px" class="mb-2 mt-4">
                      <canvas id="chart" />
                    </div>
                  </template>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <!-- COLLISIONS SECTION -->
            <v-expansion-panel>
              <v-expansion-panel-header class="text-body-2">
                <div class="d-flex" style="gap: 0.5rem">
                  <span>{{ $i18n.t('sidebar.areaReport.collisions') }} </span>
                  <v-tooltip bottom max-width="500" color="font">
                    <template #activator="{ on }">
                      <v-icon v-on="on" small color="primary">
                        mdi-information
                      </v-icon>
                    </template>
                    {{ $i18n.t('sidebar.areaReport.collisionsTooltip') }}
                  </v-tooltip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="d-flex align-center px-2 py-3" style="gap: 1rem">
                  <data-input
                    class="flex-grow-1"
                    style="width: 100%"
                    v-model="collisionsLayers"
                    v-bind="selectInputProps"
                    :multiple="true"
                    :autoConvertSelect="false"
                    :items="projectLayers"
                    itemText="name"
                    itemValue="layer_id"
                    :label="$i18n.t('sidebar.areaReport.layers')"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip small v-if="index === 0">
                        <span>{{ item.name }}</span>
                      </v-chip>
                      <span v-if="index === 1" class="grey--text text-caption">
                        {{ `(+${$i18n.tc('admin.other', collisionsLayers.length - 1)})` }}
                      </span>
                    </template>
                  </data-input>
                  <v-btn
                    @click="handleCollisions"
                    :disabled="!collisionsLayers.length"
                    :loading="isCollisionsLoading"
                    icon
                  >
                    <v-icon color="primary">mdi-play</v-icon>
                  </v-btn>
                </div>
                <div v-if="collisions" class="mt-2">
                  <div v-if="!Object.keys(collisions ?? {}).length" class="mb-5 text-caption text--disabled">
                    {{ $i18n.t('sidebar.areaReport.noCollisions') }}
                  </div>
                  <template v-else v-for="([layer, features], idx) in Object.entries(collisions ?? {})">
                    <v-row
                      :key="idx"
                      no-gutters
                      class="align-center mb-2 font-weight-medium"
                      style="gap: 0.3rem; flex-wrap: nowrap"
                    >
                      <v-col cols="auto" class="d-flex align-center">
                        <v-icon :class="{ 'is-expanded': !collapsedLayers.includes(layer) }" @click="toggleLayer(layer)"
                          >mdi-menu-right</v-icon
                        >
                      </v-col>
                      <v-col cols="auto" class="d-flex align-center">
                        <v-icon size="20" color="rgba(0, 0, 0, 0.7)">
                          {{ getDataSourceIcon(layers[layer].geometry_type) }}
                        </v-icon>
                      </v-col>
                      <v-col cols="auto" class="d-block db-name">
                        {{ layers[layer]?.name ?? '' }}
                      </v-col>
                      <v-col cols="auto">({{ features.length }})</v-col>
                    </v-row>
                    <v-row :key="`table-${idx}`" class="mb-4" no-gutters v-if="!collapsedLayers.includes(layer)">
                      <v-col class="border-bottom">
                        <v-data-table
                          @click:row="$event => onRowClick($event, layer)"
                          :headers="headers"
                          :items="features"
                          :item-style="() => 'cursor: pointer'"
                          disable-pagination
                          hide-default-footer
                          class="collisions-table"
                          dense
                        >
                          <template #[`item.options`]="{ item }">
                            <v-row no-gutters class="justify-end align-center" style="gap: 0.2rem; flex-wrap: nowrap">
                              <v-col>
                                <v-tooltip bottom color="font">
                                  <template #activator="{ on }">
                                    <span v-on="on">
                                      <v-icon @click.stop="zoomToItem(layer, item)" size="18">
                                        mdi-magnify
                                      </v-icon>
                                    </span>
                                  </template>
                                  <span>{{ $i18n.t('tooltips.zoomToFeature') }}</span>
                                </v-tooltip>
                              </v-col>
                            </v-row>
                          </template>
                        </v-data-table>
                      </v-col>
                    </v-row>
                  </template>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </template>
  </navbar-menu-tool>
</template>

<script>
import NavbarMenuTool from '@/components/NavbarMenuTool';
import DataInput from '@/components/DataInput';

import WKT from 'ol/format/WKT';
import { call, get } from 'vuex-pathify';
import { formatArea, formatLength } from '@/assets/js/mapUtils';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { GeoJSON } from 'ol/format';
import { getArea } from 'ol/sphere';
import Chart from 'chart.js';

let pieChart;

export default {
  name: 'AreaReportPanel',
  components: { NavbarMenuTool, DataInput },
  mixins: [prepareAttributesFeatureCardMixin, stylePreviewUtilities],
  props: {
    verboseName: {
      type: String,
    },
    name: {
      type: String,
    },
  },
  data: () => ({
    openedPanel: [],
    isParcelLoading: false,
    isParcelFetched: false,
    tooltipAnchorId: null,
    isMenuOpen: false,
    collisionsLayers: [],
    collapsedLayers: [],
    isCollisionsLoading: false,
    collisions: null,
    isCoverageLoading: false,
    coverage: null,
    coverageData: {
      layer: null,
      attribute: null,
    },
    infos: [],
  }),
  computed: {
    projectElements: get('layers/project@layers'),
    layers: get('layers/layers'),
    datasources: get('layers/metadata'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers?.filter(layer => layer.geometry_type);
    },
    areaProjectLayers() {
      return this.projectLayers.filter(layer => ['polygon', 'multipolygon'].includes(layer.geometry_type));
    },
    coverageLayerAttributes() {
      if (!this.coverageData.layer) return [];
      const layerMetadata = this.layers[this.coverageData.layer];
      return this.$_flattenStructure(
        this.getForm(
          layerMetadata?.form_schema.elements || [],
          this.datasources[layerMetadata?.data_source_name]?.attributes_schema?.attributes || []
        )
      );
    },
    sortedCoverage() {
      return [...(this.coverage ?? [])]?.sort((a, b) => b.coverage - a.coverage) ?? [];
    },
    coverageTableHeaders() {
      return [
        {
          text: this.$i18n.t('sidebar.areaReport.class'),
          align: 'center',
          value: 'name',
        },
        {
          text: this.$i18n.t('sidebar.areaReport.areaUnit'),
          align: 'center',
          value: 'area',
        },
        {
          text: this.$i18n.t('sidebar.areaReport.coverageUnit'),
          align: 'center',
          value: 'coverage',
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$i18n.t('default.feature'),
          align: 'start',
          value: 'desc_attribute',
        },
        {
          text: '',
          align: 'end',
          sortable: false,
          value: 'options',
        },
      ];
    },
    selectInputProps() {
      return {
        dataType: {
          name: 'select',
        },
        hideDetails: true,
      };
    },
    geometryInput() {
      return {
        name: 'areaParcel',
        disableEditing: true,
        loading: this.isParcelLoading,
        buttonProps: { translationPath: 'sidebar.areaReport.selectParcel', small: true },
        dataType: {
          name: 'geometry',
        },
        geometryType: 'Point',
      };
    },
  },
  methods: {
    searchUldkParcelByXY: call('search/searchUldkParcelByXY'),
    getAreaCollisions: call('search/getAreaCollisions'),
    getAreaLandCover: call('search/getAreaLandCover'),
    handleCoverage() {
      localStorage.setItem('area_report__coverage_data', JSON.stringify(this.coverageData));
      this.refreshCoverage();
    },
    getCoverageChartData(coverageData) {
      const layerStyle = this.projectLayers.find(layer => layer.id === this.coverageData.layer)?.style;
      const attributeName = this.coverageLayerAttributes.find(attr => attr.id === this.coverageData.attribute)
        ?.attribute;
      return coverageData.map(item => ({
        value: item.coverage,
        color:
          layerStyle.uniques?.property === attributeName
            ? layerStyle.uniques?.values[item.name]?.['fill-color'] || layerStyle['fill-color']
            : layerStyle['fill-color'],
        label: item.name,
      }));
    },
    generateCoverageChart(coverageData) {
      const canvas = document.getElementById('chart');
      if (!canvas) return;
      if (pieChart) pieChart.destroy();
      const coverageDataChart = this.getCoverageChartData(coverageData);
      const data = {
        datasets: [
          {
            data: coverageDataChart.map(item => item.value),
            backgroundColor: coverageDataChart.map(item => item.color),
          },
        ],
        labels: coverageDataChart.map(item => item.label),
      };
      pieChart = new Chart(canvas, {
        type: 'pie',
        data: data,
        options: {
          maintainAspectRatio: false,
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const coverage = data.datasets[0].data[tooltipItem.index];
                return `${data.labels[tooltipItem.index]}: ${this.getValueText('coverage', coverage)}%`;
              },
            },
          },
          legend: {
            align: 'left',
            position: 'right',
          },
        },
      });
    },
    async refreshCoverage() {
      this.isCoverageLoading = true;
      try {
        const coverageData = await this.getAreaLandCover({
          geometry: this.parcelGeometry,
          attribute_id: this.coverageData.attribute,
          layer_id: this.coverageData.layer,
        });
        const coveragePercentageSum = coverageData.reduce((acc, item) => acc + item.coverage, 0);
        const remainingPercentage = 100 - coveragePercentageSum < 0 ? 0 : 100 - coveragePercentageSum;
        if (remainingPercentage.toFixed(2) > 0) {
          const olGeometry = new GeoJSON().readGeometry(this.parcelGeometry, {
            featureProjection: this.$_config.defaultEpsg || 'EPSG:3857',
            dataProjection: this.$_config.defaultEpsg || 'EPSG:3857',
          });
          const wholeArea = getArea(olGeometry);
          const remainingArea = wholeArea * (remainingPercentage / 100);
          coverageData.push({
            name: this.$i18n.t('sidebar.areaReport.noData'),
            area: remainingArea,
            coverage: remainingPercentage,
          });
        }
        this.coverage = coverageData;
        this.$nextTick(() => this.generateCoverageChart(coverageData));
      } catch (error) {
        console.error(error);
      } finally {
        this.isCoverageLoading = false;
      }
    },
    handleCollisions() {
      localStorage.setItem('area_report__collisions_layers', JSON.stringify(this.collisionsLayers));
      this.refreshCollisions();
    },
    async refreshCollisions() {
      this.isCollisionsLoading = true;
      try {
        this.collisions = await this.getAreaCollisions({
          geometry: this.parcelGeometry,
          layers: this.collisionsLayers,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isCollisionsLoading = false;
      }
    },
    copyToClipboard(event, text, anchor) {
      event.stopPropagation();
      navigator.clipboard.writeText(text);
      this.tooltipAnchorId = anchor;
      setTimeout(() => {
        this.tooltipAnchorId = null;
      }, 1500);
    },
    getValueText(name, value, withUnits = false) {
      switch (name) {
        case 'area': {
          const parsedValue = (value / 10000).toFixed(2);
          return withUnits ? `${parsedValue}ha` : parsedValue;
        }
        case 'coverage': {
          const parsedValue = value.toFixed(2);
          return withUnits ? `${parsedValue}%` : parsedValue;
        }
        default:
          return value;
      }
    },
    getCoverageText(args) {
      if (!this.coverage) return;
      return this.coverage
        .reduce((acc, item) => {
          let newLine = `${item.name}: `;
          const newMap = args.map(ar => this.getValueText(ar, item[ar], true));
          newLine += newMap.join('; ');
          return [...acc, newLine];
        }, [])
        ?.join(', ');
    },
    toggleLayer(layer) {
      if (this.collapsedLayers.includes(layer)) {
        this.collapsedLayers = this.collapsedLayers.filter(l => l !== layer);
      } else {
        this.collapsedLayers.push(layer);
      }
    },
    onRowClick(item, layer) {
      this.$router.push({
        name: 'layerFeature',
        params: { lid: layer, fid: item.feature_id },
      });
    },
    clearGeometry() {
      this.$root.$emit('toggleCustomVectorLayer', { id: 'area-report' });
      this.isParcelFetched = false;
      this.collapsedLayers = this.openedPanel = this.infos = [];
      this.collisions = this.coverage = null;
    },
    zoomToItem(layer, item) {
      this.$root.$emit('zoomToFeature', layer, 'layers', item.feature_id);
    },
    changeCoverageLayer() {
      if (!this.coverageData.attribute) return;
      if (!this.coverageLayerAttributes.includes(this.coverageData.attribute)) {
        this.coverageData.attribute = null;
      }
    },
    async onGeometryChanged(geometry) {
      this.isParcelLoading = true;
      this.$refs['geometry-input']?.$refs['data-input']?.toggleOff();
      const coords = geometry.coordinates;
      this.isParcelFetched = false;
      try {
        await this.getParcel(coords);
        this.isParcelFetched = true;
      } catch (error) {
        console.error(error);
      } finally {
        this.isParcelLoading = false;
      }
    },
    async getParcel(coords) {
      /** Fetching ULDK */
      let projectionEpsg = this.$_config.defaultEpsg;
      const r = await this.searchUldkParcelByXY({
        coords: {
          x: coords[0],
          y: coords[1],
          epsg: this.$_config.defaultEpsg.split(':')[1],
          params: ['geom_wkt', 'numer', 'teryt', 'voivodeship', 'commune', 'county', 'region'],
        },
      });
      if (r !== null && r.error) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.notFoundPlotWithError', { error: r.error }),
        });
        throw new Error(r.error);
      }
      const noDataText = 'null';
      const terytNumber = r.teryt ?? noDataText;
      let promisesArray = [];

      /** Preparing parcel geometry */
      let wkt = r.geom_wkt;
      if (wkt.indexOf(';') >= 0) {
        projectionEpsg = `EPSG:${wkt.split(';')[0].split('=')[1]}`;
        wkt = wkt.split(';')[1];
      }
      const parcelGeometry = new WKT().readGeometry(wkt, {
        featureProjection: this.$_config.defaultEpsg || 'EPSG:4326',
        dataProjection: projectionEpsg || 'EPSG:4326',
      });
      this.parcelGeometry = {
        type: 'Polygon',
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg || 'EPSG:4326',
          },
        },
        coordinates: parcelGeometry.getCoordinates(),
      };

      /** Reading localstorage data about collisions */
      const collisionsLayers = localStorage.getItem('area_report__collisions_layers');
      if (collisionsLayers) {
        const parsedLayers = JSON.parse(collisionsLayers);
        const newLayers = parsedLayers.reduce((acc, layer) => {
          if (this.projectLayers.find(l => l.layer_id === layer)) {
            acc.push(layer);
          }
          return acc;
        }, []);
        this.collisionsLayers = newLayers;
        if (this.collisionsLayers.length) {
          promisesArray.push(this.refreshCollisions());
        }
      }

      /** Reading localstorage data about coverage */
      const coverageData = localStorage.getItem('area_report__coverage_data');
      if (coverageData) {
        const parsedData = JSON.parse(coverageData);
        if (this.projectLayers.find(l => l.layer_id === parsedData.layer)) {
          this.coverageData.layer = parsedData.layer;
          if ([...this.coverageLayerAttributes].find(attr => attr.id === parsedData.attribute)) {
            this.coverageData.attribute = parsedData.attribute;
            promisesArray.push(this.refreshCoverage());
          }
        } else {
          this.coverageData = {
            layer: null,
            attribute: null,
          };
        }
      }
      await Promise.all(promisesArray);

      /** Adding geometry to map view */
      this.$root.$emit('toggleCustomVectorLayer', {
        id: 'area-report',
        layerParams: {
          style: [
            new Style({
              fill: new Fill({
                color: 'rgba(0, 0, 0, 0)',
              }),
              stroke: new Stroke({
                color: '#BF360C',
                width: 3,
              }),
              text: new Text({
                text: terytNumber,
                font: 'normal 14px sans-serif',
                fill: new Fill({ color: 'rgba(191, 54, 12, 1)' }),
                stroke: new Stroke({
                  color: '#FFFFFF',
                  opacity: 1,
                  width: 3,
                  dash: [],
                }),
                overflow: true,
              }),
            }),
          ],
        },
        geometry: {
          type: 'Feature',
          properties: {},
          geometry: this.parcelGeometry,
        },
      });

      /** Setting parcel metadata */
      this.infos = [
        [['number', terytNumber]],
        [['voivodeship', r.voivodeship ?? noDataText]],
        [['county', r.county ?? noDataText]],
        [['commune', r.commune ?? noDataText]],
        [['region', r.region ?? noDataText]],
        [
          ['area', formatArea(parcelGeometry)],
          ['circuit', formatLength(parcelGeometry)],
        ],
      ];
    },
  },
  beforeDestroy() {
    this.$refs['geometry-input']?.$refs['data-input']?.toggleOff();
    this.clearGeometry();
  },
};
</script>

<style lang="scss" scoped>
.land-cover-table {
  @include border(all, 1px);
}
:deep(.v-data-table-header th) {
  position: relative !important;
}
:deep(.v-data-table-header__icon) {
  position: absolute;
  right: 5px;
  top: calc(50% - 8px);
}
:deep(.v-expansion-panel-header) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  min-height: 36px !important;
}
:deep(.v-expansion-panel-content__wrap) {
  padding: 0 !important;
}

.border-bottom {
  @include border(bottom);
}

.is-expanded {
  transform: rotate(90deg);
}
</style>
